import { post, getRaw, postPublic } from '../api.js';
import { decryptGrant, decryptWithLinkSecret, decryptGrantFileBlob, decryptFileBlobWithLinkSecret, rewrapWithOwnKeys, rewrapFileKeyWithOwnKeys, rewrapItemKeyWithOwnKeys, base64urlDecode, base64urlEncode, hasKeys, unwrapGrantItemKey, unwrapGrantItemKeyWithLinkSecret, decryptBlobWithKey, sha256hex, decryptClaimSecret, wipe } from '../crypto.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { navigate } from '../router.js';
import { toast } from '../dialog.js';
import { buildPlainZip, ZipStreamWriter } from '../zip.js';
import { createStreamDownload } from '../stream-download.js';
import { getGrantAcceptSecret, clearGrantAccept } from '../auth-store.js';

export async function renderGrantAccess(container, params) {
  const id = params.id;
  setTopbar('Grant Access', '');
  container.innerHTML = '<p class="loading">Accessing grant...</p>';

  try {
    const grant = await post(`/grants/${id}/access`, { operation: 'view' });

    const grantKeyBytes = new Uint8Array(grant.wrapped_key);
    const grantNonce = Array.from(grantKeyBytes.slice(0, 24));
    const grantCiphertext = Array.from(grantKeyBytes.slice(24));

    const hasFileKey = grant.file_wrapped_key && grant.file_wrapped_key.length > 0;
    const isGroupGrant = !!(grant.group_items && grant.group_id);

    let plaintext;
    let isLinkSecret = false;
    let linkSecret = null;

    if (!grant.ephemeral_pubkey || grant.ephemeral_pubkey.length === 0) {
      let storedSecret = getGrantAcceptSecret();
      if (!storedSecret || !hasKeys()) {
        // No stored secret — grant hasn't been accepted via the share link yet
        renderPendingAccept(container, id);
        return;
      }

      linkSecret = base64urlDecode(storedSecret);
      isLinkSecret = true;
      plaintext = decryptWithLinkSecret(grantCiphertext, grantNonce, linkSecret, grant.encrypted_blob, undefined, grant.grantor_id);

      try {
        const rewrapped = rewrapWithOwnKeys(grantCiphertext, grantNonce, linkSecret);
        const newWrappedKey = [...rewrapped.nonce, ...rewrapped.wrapped_key];
        const rewrapBody = { wrapped_key: newWrappedKey, ephemeral_pubkey: rewrapped.ephemeral_pubkey };
        if (hasFileKey) {
          rewrapBody.file_wrapped_key = rewrapFileKeyWithOwnKeys(grant.file_wrapped_key, linkSecret);
        }
        // Rewrap per-item keys for group grants
        const oldItemKeys = grant.wrapped_item_keys;
        if (oldItemKeys && typeof oldItemKeys === 'object') {
          const newItemKeys = {};
          for (const [itemId, entry] of Object.entries(oldItemKeys)) {
            try {
              const rw = rewrapItemKeyWithOwnKeys(entry.wrapped_key, entry.nonce, linkSecret);
              const newEntry = { wrapped_key: [...rw.wrapped_key], nonce: [...rw.nonce], ephemeral_pubkey: [...rw.ephemeral_pubkey] };
              if (entry.file_wrapped_key) {
                newEntry.file_wrapped_key = rewrapFileKeyWithOwnKeys(entry.file_wrapped_key, linkSecret);
              }
              newItemKeys[itemId] = newEntry;
            } catch (_) {
              newItemKeys[itemId] = entry; // keep original if rewrap fails
            }
          }
          rewrapBody.wrapped_item_keys = newItemKeys;
        }
        await post(`/grants/${id}/rewrap`, rewrapBody);
        clearGrantAccept();
      } catch (rewrapErr) {
        console.warn('rewrap failed:', rewrapErr);
      }
    } else {
      plaintext = decryptGrant(grant.wrapped_key, grant.ephemeral_pubkey, grant.encrypted_blob, grant.grantor_id);
    }

    const policy = typeof grant.policy === 'string' ? JSON.parse(grant.policy) : grant.policy;

    if (isGroupGrant) {
      renderGroupGrant(container, grant, plaintext, policy, isLinkSecret, linkSecret, id);
    } else {
      renderSingleItemGrant(container, grant, plaintext, policy, isLinkSecret, linkSecret, hasFileKey, id);
    }
  } catch (err) {
    container.innerHTML = `
      <div class="msg msg-error">${esc(err.message)}</div>
      <div class="actions"><a href="#/grants" class="btn btn-secondary">Back to Grants</a></div>`;
  }
}

function renderGroupGrant(container, grant, plaintext, policy, isLinkSecret, linkSecret, grantId) {
  let groupName = 'Collection';
  try {
    const env = JSON.parse(plaintext);
    if (env && env.name) groupName = env.name;
  } catch {}

  const wrappedItemKeys = grant.wrapped_item_keys || {};
  const itemDisplays = [];

  for (const item of grant.group_items) {
    const itemKeyData = wrappedItemKeys[item.id];
    let itemName = 'Untitled';
    let isFile = false;
    let fileMimeType = 'application/octet-stream';
    let textContent = null;

    if (itemKeyData) {
      let itemKey;
      try {
        if (isLinkSecret) {
          itemKey = unwrapGrantItemKeyWithLinkSecret(itemKeyData.wrapped_key, itemKeyData.nonce, linkSecret);
        } else {
          itemKey = unwrapGrantItemKey(itemKeyData.wrapped_key, itemKeyData.nonce, itemKeyData.ephemeral_pubkey);
        }

        const decryptedText = decryptBlobWithKey(itemKey, item.encrypted_blob, grant.grantor_id);
        const env = JSON.parse(decryptedText);
        itemName = env.name || 'Untitled';
        if (env.filename) {
          isFile = true;
          fileMimeType = env.mime_type || 'application/octet-stream';
        } else if (env.content !== undefined) {
          textContent = env.content;
        }
      } catch (e) {
        console.warn('Failed to decrypt group item', item.id, e);
      } finally {
        wipe(itemKey);
      }
    }

    itemDisplays.push({ id: item.id, name: itemName, isFile, fileMimeType, textContent, hasFileKey: !!(itemKeyData && itemKeyData.file_wrapped_key) });
  }

  container.innerHTML = `
    <div class="card">
      <div class="card-header"><span class="card-title">${esc(groupName)}</span><span class="badge badge-encrypted">Collection</span></div>
      <div class="detail-row"><span class="detail-label">Grant ID</span><span style="font-size:0.8rem" class="mono">${grant.id}</span></div>
      <div class="detail-row"><span class="detail-label">Status</span><span class="badge badge-${grant.status}">${grant.status}</span></div>
      <div class="detail-row"><span class="detail-label">Views</span><span class="mono">${grant.view_count}${policy?.max_views ? '/' + policy.max_views : ''}</span></div>
      ${policy?.expires_at ? `<div class="detail-row"><span class="detail-label">Expires</span><span>${new Date(policy.expires_at).toLocaleString()}</span></div>` : ''}
      ${policy?.allowed_ops ? `<div class="detail-row"><span class="detail-label">Allowed</span><span>${policy.allowed_ops.join(', ')}</span></div>` : ''}
      <div class="detail-row"><span class="detail-label">Items</span><span>${itemDisplays.length}</span></div>
    </div>

    <div class="card">
      <div class="card-header"><span class="card-title">Items in Collection</span></div>
      ${itemDisplays.map(item => `
        <div class="grant-group-item" data-item-id="${item.id}">
          <div class="detail-row" style="align-items:center;cursor:pointer;border-bottom:none;padding:8px 0">
            <span style="display:flex;align-items:center;gap:6px">
              <i data-lucide="${item.isFile ? 'file' : 'lock'}" style="width:14px;height:14px"></i>
              <span style="font-weight:500">${esc(item.name)}</span>
              <span class="badge ${item.isFile ? 'badge-drop' : 'badge-encrypted'}" style="font-size:10px">${item.isFile ? 'File' : 'Text'}</span>
            </span>
            <span style="display:flex;align-items:center;gap:4px">
              ${item.isFile && item.hasFileKey ? `<button class="btn btn-ghost btn-sm download-item-btn" data-item-id="${item.id}"><i data-lucide="download" style="width:14px;height:14px"></i></button>` : ''}
              <button class="btn btn-ghost btn-sm view-item-btn" data-item-id="${item.id}"><i data-lucide="eye" style="width:14px;height:14px"></i></button>
            </span>
          </div>
          <div class="grant-item-detail" id="item-detail-${item.id}" style="display:none;padding:0 0 12px;border-bottom:1px solid var(--border)"></div>
        </div>
      `).join('')}
    </div>

    <div class="actions">
      ${policy?.allowed_ops?.includes('download') && itemDisplays.some(i => (i.isFile && i.hasFileKey) || i.textContent !== null) ? `<button class="btn btn-primary btn-sm" id="download-all-btn"><i data-lucide="archive" style="width:14px;height:14px"></i> Download All as ZIP</button>` : ''}
      ${policy?.allowed_ops?.includes('re-share') ? `<button class="btn btn-primary btn-sm" id="reshare-btn"><i data-lucide="forward" style="width:14px;height:14px"></i> Re-share</button>` : ''}
      <a href="#/grants" class="btn btn-secondary">Back to Grants</a>
    </div>
  `;
  lucideIcons();

  // Download All as ZIP
  const downloadAllBtn = document.getElementById('download-all-btn');
  if (downloadAllBtn) {
    downloadAllBtn.onclick = async () => {
      downloadAllBtn.disabled = true;
      const origHtml = downloadAllBtn.innerHTML;
      try {
        const downloadable = itemDisplays.filter(i => (i.isFile && i.hasFileKey) || i.textContent !== null);
        const zipFilename = (groupName || 'collection') + '.zip';

        // Try streaming path
        const stream = await createStreamDownload(zipFilename);
        if (stream) {
          try {
            const zipWriter = new ZipStreamWriter((chunk) => stream.writer.write(chunk));
            for (let idx = 0; idx < downloadable.length; idx++) {
              downloadAllBtn.textContent = `Downloading ${idx + 1}/${downloadable.length}...`;
              const { fileName, fileData } = await fetchAndDecryptItem(downloadable[idx], grantId, wrappedItemKeys, isLinkSecret, linkSecret, grant.grantor_id);
              if (fileData) {
                await zipWriter.addEntry(fileName, fileData);
              }
            }
            downloadAllBtn.textContent = 'Finalizing...';
            await zipWriter.finish();
            stream.writer.close();
            stream.cleanup();
          } catch (err) {
            try { stream.writer.abort(err.message); } catch (_) {}
            stream.cleanup();
            throw err;
          }
        } else {
          // Fallback: in-memory
          const zipFiles = [];
          for (let idx = 0; idx < downloadable.length; idx++) {
            downloadAllBtn.textContent = `Downloading ${idx + 1}/${downloadable.length}...`;
            const { fileName, fileData } = await fetchAndDecryptItem(downloadable[idx], grantId, wrappedItemKeys, isLinkSecret, linkSecret, grant.grantor_id);
            if (fileData) {
              zipFiles.push({ name: fileName, data: fileData });
            }
          }
          downloadAllBtn.textContent = 'Building ZIP...';
          const zipData = await buildPlainZip(zipFiles);
          const blob = new Blob([zipData], { type: 'application/zip' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = zipFilename;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        toast(`Download failed: ${e.message}`);
      } finally {
        downloadAllBtn.disabled = false;
        downloadAllBtn.innerHTML = origHtml;
        lucideIcons();
      }
    };
  }

  const reshareBtn = document.getElementById('reshare-btn');
  if (reshareBtn) {
    reshareBtn.onclick = () => navigate(`#/grants/new?group_id=${grant.group_id}&from_grant=${grantId}`);
  }

  // View item detail buttons (expand inline)
  container.querySelectorAll('.view-item-btn').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      const itemId = btn.dataset.itemId;
      const detailEl = document.getElementById(`item-detail-${itemId}`);
      if (!detailEl) return;

      // Toggle visibility
      if (detailEl.style.display !== 'none') {
        detailEl.style.display = 'none';
        return;
      }

      const item = itemDisplays.find(i => i.id === itemId);
      if (!item) return;

      if (item.isFile) {
        detailEl.innerHTML = `
          <div style="padding:12px;background:var(--bg-elevated);border-radius:8px;margin-top:8px">
            <div style="font-size:13px;color:var(--text-secondary);margin-bottom:4px">Encrypted file</div>
            <div style="font-size:14px;font-weight:500">${esc(item.name)}</div>
            <div style="font-size:12px;color:var(--text-secondary);margin-top:2px">${esc(item.fileMimeType)}</div>
            ${item.hasFileKey ? `<button class="btn btn-secondary btn-sm download-detail-btn" data-item-id="${item.id}" style="margin-top:8px"><i data-lucide="download" style="width:14px;height:14px"></i> Download</button>` : '<div class="msg msg-error" style="margin-top:8px;font-size:12px">File key not available</div>'}
          </div>`;
        // Bind the inline download button
        const dlBtn = detailEl.querySelector('.download-detail-btn');
        if (dlBtn) {
          dlBtn.onclick = (ev) => { ev.stopPropagation(); downloadGroupItem(dlBtn, itemId, grantId, wrappedItemKeys, isLinkSecret, linkSecret, itemDisplays, grant.grantor_id); };
        }
      } else if (item.textContent !== null) {
        detailEl.innerHTML = `
          <div style="padding:12px;background:var(--bg-elevated);border-radius:8px;margin-top:8px">
            <div style="font-size:12px;color:var(--text-secondary);margin-bottom:8px;text-transform:uppercase;letter-spacing:.5px;font-weight:500">Content</div>
            <div class="code-block" style="max-height:200px">${esc(item.textContent)}</div>
          </div>`;
      } else {
        detailEl.innerHTML = `
          <div style="padding:12px;background:var(--bg-elevated);border-radius:8px;margin-top:8px">
            <div class="msg msg-error" style="font-size:12px;margin:0">Could not decrypt item content</div>
          </div>`;
      }

      detailEl.style.display = '';
      lucideIcons();
    };
  });

  // Row click also toggles detail
  container.querySelectorAll('.grant-group-item .detail-row').forEach(row => {
    row.onclick = (e) => {
      if (e.target.closest('button')) return;
      const itemId = row.closest('.grant-group-item').dataset.itemId;
      const viewBtn = container.querySelector(`.view-item-btn[data-item-id="${itemId}"]`);
      if (viewBtn) viewBtn.click();
    };
  });

  // Individual file download buttons (in the header row)
  container.querySelectorAll('.download-item-btn').forEach(btn => {
    btn.onclick = async (e) => {
      e.stopPropagation();
      await downloadGroupItem(btn, btn.dataset.itemId, grantId, wrappedItemKeys, isLinkSecret, linkSecret, itemDisplays, grant.grantor_id);
    };
  });
}

async function fetchAndDecryptItem(item, grantId, wrappedItemKeys, isLinkSecret, linkSecret, grantorId) {
  let fileData = null;
  let fileName = item.name || 'untitled';

  try {
    if (item.isFile && item.hasFileKey) {
      const ab = await getRaw(`/grants/${grantId}/blob?item_id=${item.id}`);
      const encryptedData = new Uint8Array(ab);
      const itemKeyData = wrappedItemKeys[item.id];
      const fwk = new Uint8Array(itemKeyData.file_wrapped_key);

      if (isLinkSecret) {
        fileData = decryptFileBlobWithLinkSecret(fwk, linkSecret, encryptedData, grantorId);
      } else {
        fileData = decryptGrantFileBlob(fwk, encryptedData, grantorId);
      }
    } else if (item.textContent !== null) {
      fileData = new TextEncoder().encode(item.textContent);
      if (!fileName.includes('.')) fileName += '.txt';
    }
  } catch (_) {
    fileData = new TextEncoder().encode(`Error downloading: ${_.message}`);
    fileName = (item.name || 'error') + '.error.txt';
  }

  if (fileData && !(fileData instanceof Uint8Array)) {
    fileData = new Uint8Array(fileData);
  }

  const safeName = fileName.replace(/[/\\:*?"<>|]/g, '_').slice(0, 200) || 'unnamed';
  return { fileName: safeName, fileData };
}

async function downloadGroupItem(btn, itemId, grantId, wrappedItemKeys, isLinkSecret, linkSecret, itemDisplays, grantorId) {
  btn.disabled = true;
  const origHtml = btn.innerHTML;
  btn.textContent = '...';
  try {
    const ab = await getRaw(`/grants/${grantId}/blob?item_id=${itemId}`);
    const encryptedData = new Uint8Array(ab);
    const itemKeyData = wrappedItemKeys[itemId];
    const fwk = new Uint8Array(itemKeyData.file_wrapped_key);

    let decrypted;
    if (isLinkSecret) {
      decrypted = decryptFileBlobWithLinkSecret(fwk, linkSecret, encryptedData, grantorId);
    } else {
      decrypted = decryptGrantFileBlob(fwk, encryptedData, grantorId);
    }

    const item = itemDisplays.find(i => i.id === itemId);
    const blob = new Blob([decrypted], { type: item?.fileMimeType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = item?.name || 'download';
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast(`Download failed: ${e.message}`);
  } finally {
    btn.disabled = false;
    btn.innerHTML = origHtml;
    lucideIcons();
  }
}

function renderSingleItemGrant(container, grant, plaintext, policy, isLinkSecret, linkSecret, hasFileKey, grantId) {
  let displayContent = plaintext;
  let envelopeName = null;
  let isFile = false;
  let fileMimeType = 'application/octet-stream';
  try {
    const env = JSON.parse(plaintext);
    if (env && env.filename) {
      isFile = true;
      envelopeName = env.name || env.filename;
      fileMimeType = env.mime_type || 'application/octet-stream';
      displayContent = null;
    } else if (env && env.content !== undefined) {
      displayContent = env.content;
      envelopeName = env.name;
    }
  } catch { /* not an envelope */ }

  container.innerHTML = `
    <div class="card">
      <div class="detail-row"><span class="detail-label">Grant ID</span><span style="font-size:0.8rem" class="mono">${grant.id}</span></div>
      <div class="detail-row"><span class="detail-label">Status</span><span class="badge badge-${grant.status}">${grant.status}</span></div>
      <div class="detail-row"><span class="detail-label">Views</span><span class="mono">${grant.view_count}${policy?.max_views ? '/' + policy.max_views : ''}</span></div>
      ${policy?.expires_at ? `<div class="detail-row"><span class="detail-label">Expires</span><span>${new Date(policy.expires_at).toLocaleString()}</span></div>` : ''}
      ${policy?.ttl_seconds ? `<div class="detail-row"><span class="detail-label">TTL</span><span>${policy.ttl_seconds >= 86400 ? Math.round(policy.ttl_seconds/86400) + 'd' : policy.ttl_seconds >= 3600 ? Math.round(policy.ttl_seconds/3600) + 'h' : Math.round(policy.ttl_seconds/60) + 'm'} after first access</span></div>` : ''}
      ${policy?.allowed_ops ? `<div class="detail-row"><span class="detail-label">Allowed</span><span>${policy.allowed_ops.join(', ')}</span></div>` : ''}
      ${envelopeName ? `<div class="detail-row"><span class="detail-label">Name</span><span>${esc(envelopeName)}</span></div>` : ''}
    </div>
    ${isFile ? `
      <div class="card">
        <div class="card-header"><span class="card-title">Encrypted File</span></div>
        <p>${esc(envelopeName)} <span style="color:var(--text-secondary)">(${esc(fileMimeType)})</span></p>
        ${hasFileKey ? '<button class="btn btn-primary btn-sm" id="download-file-btn" style="margin-top:12px;width:auto"><i data-lucide="download" style="width:14px;height:14px"></i> Download File</button>' :
          '<div class="msg msg-error" style="margin-top:8px">File key not available.</div>'}
      </div>
    ` : `
      <div class="card">
        <div class="card-header"><span class="card-title">Decrypted Content</span></div>
        <div class="code-block">${esc(displayContent)}</div>
      </div>
    `}
    <div class="actions">
      ${policy?.allowed_ops?.includes('re-share') ? `<button class="btn btn-primary btn-sm" id="reshare-btn"><i data-lucide="forward" style="width:14px;height:14px"></i> Re-share</button>` : ''}
      <a href="#/grants" class="btn btn-secondary">Back to Grants</a>
    </div>
  `;
  lucideIcons();

  const reshareBtn = document.getElementById('reshare-btn');
  if (reshareBtn) {
    reshareBtn.onclick = () => navigate(`#/grants/new?item_id=${grant.item_id}&from_grant=${grantId}`);
  }

  if (isFile && hasFileKey) {
    document.getElementById('download-file-btn').onclick = async () => {
      const btn = document.getElementById('download-file-btn');
      btn.disabled = true;
      btn.textContent = 'Downloading...';
      try {
        const ab = await getRaw(`/grants/${grantId}/blob`);
        const encryptedData = new Uint8Array(ab);

        let decrypted;
        if (isLinkSecret) {
          decrypted = decryptFileBlobWithLinkSecret(grant.file_wrapped_key, linkSecret, encryptedData, grant.grantor_id);
        } else {
          decrypted = decryptGrantFileBlob(grant.file_wrapped_key, encryptedData, grant.grantor_id);
        }

        const blob = new Blob([decrypted], { type: fileMimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = envelopeName;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        toast(`Download failed: ${e.message}`);
      } finally {
        btn.disabled = false;
        btn.innerHTML = '<i data-lucide="download" style="width:14px;height:14px"></i> Download File';
        lucideIcons();
      }
    };
  }
}

function renderPendingAccept(container, grantId) {
  container.innerHTML = `
    <div class="card" style="max-width:500px;margin:40px auto;text-align:center">
      <div style="width:48px;height:48px;border-radius:50%;background:var(--bg-elevated);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px">
        <i data-lucide="mail" style="width:24px;height:24px;color:var(--accent)"></i>
      </div>
      <h2 style="margin-bottom:8px">Check Your Email</h2>
      <p style="color:var(--text-secondary);margin-bottom:20px;font-size:14px">
        This item was shared via a secure link. Open the email from BlindKeep and click the link to unlock it.
        Once unlocked, the item will be saved to your vault permanently.
      </p>
      <div class="actions" style="justify-content:center">
        <a href="#/grants" class="btn btn-secondary">Back to Grants</a>
      </div>
    </div>
  `;
  lucideIcons();
}
